import React, { useMemo, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { FiMinus, FiPlus, FiTrash2 } from 'react-icons/fi'
import {
  formatProductPrice,
  getProductPrimaryImage,
} from '../api/productApi'
import { useCart } from '../context/CartContext'
import RelatedProductsSection from '../components/UI/RelatedProductsSection'
import fallbackProductImage from '../assets/images/FortuneBasket.png'

function unitPrice(product) {
  const list = product?.price != null ? Number(product.price) : null
  const sale =
    product?.discount_price != null ? Number(product.discount_price) : null
  if (sale != null && list != null && sale < list) return sale
  if (list != null && Number.isFinite(list)) return list
  if (sale != null && Number.isFinite(sale)) return sale
  return null
}

export default function CartPage() {
  const {
    items,
    isSyncing,
    isAuthenticated,
    setQuantity,
    removeFromCart,
  } = useCart()
  const [busyId, setBusyId] = useState(null)

  const excludeIds = useMemo(
    () => items.map((item) => item.product?.product_id).filter(Boolean),
    [items]
  )

  const total = useMemo(
    () =>
      items.reduce((sum, item) => {
        const price = unitPrice(item.product)
        if (price == null) return sum
        return sum + price * item.quantity
      }, 0),
    [items]
  )

  const handleQty = async (productId, next) => {
    setBusyId(productId)
    try {
      await setQuantity(productId, next)
    } finally {
      setBusyId(null)
    }
  }

  const handleRemove = async (productId) => {
    setBusyId(productId)
    try {
      await removeFromCart(productId)
    } finally {
      setBusyId(null)
    }
  }

  if (!isAuthenticated) {
    return (
      <section className="space-top">
        <div className="container py-5">
          <div className="text-center mb-5">
            <h2 className="text-lora font-semibold mb-3">Cart</h2>
            <p className="light-color mb-4">
              Please log in to view and manage your cart.
            </p>
            <NavLink
              to="/login"
              state={{ from: '/cart' }}
              className="first-btn d-inline-block"
              style={{ width: 'auto', padding: '14px 28px' }}
            >
              Login
            </NavLink>
          </div>
          <RelatedProductsSection />
        </div>
      </section>
    )
  }

  if (isSyncing && items.length === 0) {
    return (
      <section className="space-top">
        <div className="container py-5">
          <div className="text-center mb-5">
            <h2 className="text-lora font-semibold mb-3">Cart</h2>
            <p className="light-color">Loading your cart...</p>
          </div>
          <RelatedProductsSection />
        </div>
      </section>
    )
  }

  if (items.length === 0) {
    return (
      <section className="space-top">
        <div className="container py-5">
          <div className="text-center mb-5">
            <h2 className="text-lora font-semibold mb-3">Cart</h2>
            <p className="light-color mb-4">
              Your cart is empty. Add products from the shop to continue.
            </p>
            <NavLink
              to="/shop"
              className="first-btn d-inline-block"
              style={{ width: 'auto', padding: '14px 28px' }}
            >
              Browse Shop
            </NavLink>
          </div>
          <RelatedProductsSection />
        </div>
      </section>
    )
  }

  return (
    <section className="space-top">
      <div className="container py-5">
        <div className="d-flex justify-content-between align-items-end mb-4 flex-wrap gap-2">
          <div>
            <h2 className="text-lora font-semibold mb-1">Cart</h2>
            <p className="light-color mb-0">
              {items.length} {items.length === 1 ? 'item' : 'items'} in your cart
            </p>
          </div>
          <NavLink
            to="/shop"
            className="text-[#CDA202] no-underline fw-semibold"
          >
            Continue shopping →
          </NavLink>
        </div>

        <div className="row g-4">
          <div className="col-12 col-lg-8">
            <div className="d-flex flex-column gap-3">
              {items.map(({ product, quantity, cartId }) => {
                const productId = String(product.product_id)
                const imageUrl =
                  getProductPrimaryImage(product) || fallbackProductImage
                const price = unitPrice(product)
                const busy = busyId === productId

                return (
                  <article
                    key={cartId || productId}
                    className="cart-line-item second-bg rounded-xl p-3 d-flex gap-3 align-items-center flex-wrap"
                  >
                    <NavLink
                      to={`/shop/${product.product_id}`}
                      state={product}
                      className="cart-line-item__image-wrap"
                    >
                      <img
                        src={imageUrl}
                        alt={product.product_name || 'Product'}
                        className="cart-line-item__image"
                        onError={(event) => {
                          event.currentTarget.onerror = null
                          event.currentTarget.src = fallbackProductImage
                        }}
                      />
                    </NavLink>

                    <div className="flex-grow-1 min-w-0">
                      <NavLink
                        to={`/shop/${product.product_id}`}
                        state={product}
                        className="text-lora font-semibold no-underline text-dark d-block text-truncate"
                      >
                        {product.product_name}
                      </NavLink>
                      <div className="mt-1 fw-bold">
                        {price != null ? `₹${formatProductPrice(price)}` : '-'}
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="cart-qty-btn"
                        aria-label="Decrease quantity"
                        disabled={busy}
                        onClick={() => handleQty(productId, quantity - 1)}
                      >
                        <FiMinus />
                      </button>
                      <span className="cart-qty-value">{quantity}</span>
                      <button
                        type="button"
                        className="cart-qty-btn"
                        aria-label="Increase quantity"
                        disabled={busy}
                        onClick={() => handleQty(productId, quantity + 1)}
                      >
                        <FiPlus />
                      </button>
                    </div>

                    <button
                      type="button"
                      className="cart-remove-btn"
                      aria-label="Remove from cart"
                      disabled={busy}
                      onClick={() => handleRemove(productId)}
                    >
                      <FiTrash2 />
                    </button>
                  </article>
                )
              })}
            </div>
          </div>

          <div className="col-12 col-lg-4">
            <div className="second-bg rounded-xl p-4 sticky-lg-top">
              <h3 className="text-lora font-semibold mb-3">Order Summary</h3>
              <div className="d-flex justify-content-between mb-2">
                <span className="light-color">Subtotal</span>
                <span className="fw-semibold">
                  ₹{formatProductPrice(total)}
                </span>
              </div>
              <hr />
              <div className="d-flex justify-content-between mb-4">
                <span className="fw-semibold">Total</span>
                <span className="fw-bold fs-5">
                  ₹{formatProductPrice(total)}
                </span>
              </div>
              <NavLink
                to="/order-page"
                state={{ cartItems: items }}
                className="first-btn text-center text-white no-underline d-block"
              >
                Order Now
              </NavLink>
            </div>
          </div>
        </div>

        <RelatedProductsSection excludeIds={excludeIds} />
      </div>
    </section>
  )
}
